import React, { useEffect, useMemo, useState } from "react";
import TileLayer from "ol/layer/Tile";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS";
import useLayerEvents from "../hooks/useLayerEvents";
import useLayerOpacity from "../hooks/useLayerOpacity";
import useLayerZIndex from "../hooks/useLayerZIndex";
import useWmtsCapabilities from "../hooks/useWmtsCapabilities";
import { useMapContext } from "../map/MapProvider";
import LayerContext from "./LayerContext";
/**
 * Add a WMTS layer to the map. The service URL and values needed to select
 * the correct layer/matrix set/layer style are the only required values.
 *
 * The rest is automatically fetched from GetCapabilities. **This means this
 * layer only works for CORS enabled services.**
 */
export default function WmtsLayer({ children, authKey, opacity = 1, maxZoom, minZoom, zIndex = 0, attributions, format = "image/jpeg", url, layer, crossOrigin = undefined, matrixSet, style, onPreRender, onPostRender, }) {
    const { map, view, auth } = useMapContext();
    const setLayerZIndex = useLayerZIndex(zIndex);
    const setLayerOpacity = useLayerOpacity(opacity);
    const setLayerEvents = useLayerEvents({ onPreRender, onPostRender });
    const [_layer, setLayer] = useState();
    const { capabilities } = useWmtsCapabilities(url);
    const options = useMemo(() => !!capabilities &&
        !!layer &&
        optionsFromCapabilities(capabilities, {
            layer,
            matrixSet,
            style,
            format,
            crossOrigin,
        }), [capabilities, layer, matrixSet, style, format, crossOrigin]);
    useEffect(() => {
        var _a;
        if (!options)
            return;
        const tileLoadFunction = (imageTile, src) => {
            imageTile.getImage().src =
                authKey && auth ? `${src}&${authKey}=${auth[authKey]}` : src;
        };
        // If the service reports http urls while being called via https, replace
        // the protocol with https.
        const optionsUrls = (_a = options.urls) !== null && _a !== void 0 ? _a : (options.url ? [options.url] : []);
        const originalUrl = new URL(url);
        const urls = originalUrl.protocol === "https:"
            ? optionsUrls.map((u) => {
                const url = new URL(u);
                if (url.protocol !== originalUrl.protocol &&
                    url.host === originalUrl.host) {
                    url.protocol = "https:";
                }
                return url.toString();
            })
            : optionsUrls;
        const source = new WMTS({
            ...options,
            urls,
            tileLoadFunction,
            attributions,
        });
        const tileLayer = new TileLayer({
            source,
            minZoom,
            maxZoom,
        });
        setLayerEvents(tileLayer);
        setLayerZIndex(tileLayer);
        setLayerOpacity(tileLayer);
        setLayer(tileLayer);
        map.addLayer(tileLayer);
        return () => {
            map.removeLayer(tileLayer);
            setLayer(undefined);
            setLayerEvents(undefined);
            setLayerZIndex(undefined);
            setLayerOpacity(undefined);
        };
    }, [
        map,
        view,
        attributions,
        auth,
        maxZoom,
        minZoom,
        authKey,
        options,
        setLayerEvents,
        setLayerZIndex,
        setLayerOpacity,
        url,
    ]);
    return (React.createElement(LayerContext.Provider, { value: { layer: _layer } }, children));
}
